import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { usePulseSegments } from '@pulse/react';

import { useTranslator } from '@picsart/rc/services/localization';
import useCategoryDataStore from 'templates/stores/categoryDataStore';

import { HorizontalContainer } from '@picsart/design-system/components/HorizontalContainer';
import SanitizeHtml from '@picsart/growth-rc/old/SanitizeHtml';

import SearchInput from 'components/SearchInput';

import { useCategory } from 'templates/hooks';
import TemplateCategoryFilter from 'components/TemplateCategoryFilter';
import { placeholder } from 'components/Banner/BannerWithSearch/constants';
import { allTemplates } from 'constants/templates';
import categoryNameConstruct from 'utils/categoryNameConstruct';
import { getCategoryBannerTitle } from '../../utils';
import useStyles from './styles';

const CategoryBanner = () => {
  const translate = useTranslator();

  const styles = useStyles();

  const { category, subCategory } = useParams();
  const { isSearchPage } = useCategory();
  const [query] = useSearchParams();
  const { finalCategory, newCategoryPage } = categoryNameConstruct(category, subCategory);
  // eslint-disable-next-line no-nested-ternary
  const filterDataCategory = isSearchPage ? '' : newCategoryPage ? subCategory : finalCategory;

  const { segments, loading: segmentLoading } = usePulseSegments();

  const { categoryData, fetchCategoryData } = useCategoryDataStore(state => ({
    categoryData: state.category,
    fetchCategoryData: state.fetchCategoryData,
  }));

  const filterData = [filterDataCategory, allTemplates];
  const [filterType, setFilterType] = useState<string>(
    newCategoryPage ? subCategory : finalCategory,
  );

  const title = useMemo(
    () => getCategoryBannerTitle({ query, displayName: categoryData?.display_name }),
    [categoryData?.display_name, query],
  );

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    (async () => {
      if (segmentLoading) return;
      await fetchCategoryData(category, subCategory, segments);
    })();
  }, [category, fetchCategoryData, subCategory, segments, segmentLoading]);

  return (
    <HorizontalContainer
      className={styles.root}
      dataTestId="templates-heading-wrapper"
      data-pulse-section="subcategory-banner-section"
    >
      <div className={styles.bannerContent}>
        <h1 className={styles.title}>{translate(title)}</h1>
        {!query.get('q') && (
          <SanitizeHtml
            className={styles.description}
            tag="div"
            html={translate(categoryData?.short_text)}
          />
        )}
        <div className={styles.filterAndSearchbarContainer}>
          {!isSearchPage && category && (
            <TemplateCategoryFilter
              filterData={filterData}
              filterType={filterType}
              setFilterType={setFilterType}
            />
          )}
          <SearchInput placeholder={placeholder} filterType={filterType} isTemplatePage />
        </div>
      </div>
    </HorizontalContainer>
  );
};

export default CategoryBanner;
